/* eslint-disable no-unused-vars */

import SockJS from 'sockjs-client';
import { Client } from '@stomp/stompjs';

/**
 * WebSocket service for handling real-time communication using STOMP protocol.
 * Manages connections, subscriptions, heartbeats, and automatic reconnection.
 */
class WebSocketService {
    constructor() {
        /** @type {SockJS|null} WebSocket connection instance */
        this.socket = null;
        /** @type {Client|null} STOMP client instance */
        this.stompClient = null;
        /** @type {number|null} Heartbeat interval ID */
        this.heartbeatInterval = null;
        /** @type {Map<string, Set>} Map of topic subscriptions */
        this.subscriptions = new Map();
        /** @type {Function|null} Connection state change callback */
        this.onConnectionChange = null;
        /** @type {Function|null} Reconnection callback */
        this.reconnectionCallback = null;
        /** @type {number} Number of reconnection attempts */
        this.reconnectAttempts = 0;
        /** @type {Promise|null} Current connection promise */
        this.connectPromise = null;

        // Reset the backoff when the tab regains visibility so a throttled
        // background tab reconnects quickly once the user returns
        if (typeof document !== 'undefined') {
            document.addEventListener('visibilitychange', () => {
                if (document.visibilityState === 'visible') {
                    this.reconnectAttempts = 0;
                }
            });
        }
    }

    /**
     * Establishes a WebSocket connection with authentication.
     * @param {string} token - Authentication token
     * @returns {Promise<void>} Connection promise
     */
    async connect(token) {
        if (this.connectPromise) {
            return this.connectPromise;
        }

        this.connectPromise = new Promise((resolve, reject) => {
            this.socket = new SockJS(`${process.env.VUE_APP_API_URL}/ws`, {
                transportOptions: {
                    'xhr-streaming': {
                        headers: {
                            Authorization: `Bearer ${token}`,
                        },
                    },
                },
            });

            this.stompClient = new Client({
                webSocketFactory: () => this.socket,
                reconnectDelay: 0, // Disable automatic reconnection - we handle it manually
            });

            this.stompClient.onConnect = () => {
                this.handleConnect();
                resolve();
            };

            this.stompClient.onWebSocketClose = (event) => {
                this.handleDisconnect();
                // Don't reject here if this is during normal operation
                if (this.connectPromise) {
                    reject(new Error('WebSocket disconnected during initial connection'));
                }
            };

            this.stompClient.onStompError = (error) => {
                console.error('STOMP error:', error);
                this.handleDisconnect();
                if (this.connectPromise) {
                    reject(error);
                }
            };

            this.stompClient.activate();
        });

        try {
            await this.connectPromise;
        } finally {
            this.connectPromise = null;
        }

        return this.connectPromise;
    }

    /**
     * Handles successful connection establishment.
     * @private
     */
    handleConnect() {
        console.info('[WebSocket] Connected successfully. Reconnect attempts were:', this.reconnectAttempts);
        this.reconnectAttempts = 0;
        this.notifyConnectionChange(true);

        // Add a small delay to ensure STOMP is fully ready before resubscribing
        setTimeout(() => {
            this.resubscribeAll();
            console.info('[WebSocket] Resubscribed to', this.subscriptions.size, 'topics:', [
                ...this.subscriptions.keys(),
            ]);
        }, 100);

        this.startHeartbeat();
    }

    /**
     * Handles connection loss or disconnection.
     * @private
     */
    handleDisconnect() {
        console.warn('[WebSocket] Disconnected. Active topics:', [...this.subscriptions.keys()]);
        // Clear STOMP subscription references (but keep callbacks)
        // The old STOMP subscriptions are no longer valid after disconnect
        for (const [topic, topicData] of this.subscriptions.entries()) {
            if (topicData.subscription) {
                delete topicData.subscription;
            }
        }

        this.notifyConnectionChange(false);
        this.stopHeartbeat();
        this.scheduleReconnect();
    }

    /**
     * Notifies connection state changes to registered handler.
     * @param {boolean} connected - Current connection state
     * @private
     */
    notifyConnectionChange(connected) {
        if (this.onConnectionChange) {
            this.onConnectionChange(connected);
        }
    }

    /**
     * Sets handler for connection state changes.
     * @param {Function} handler - Callback function for connection state changes
     */
    setConnectionChangeHandler(handler) {
        this.onConnectionChange = handler;
    }

    /**
     * Subscribes to a topic with a callback for messages.
     * @param {string} topic - Topic to subscribe to
     * @param {Function} callback - Callback function for received messages
     */
    async subscribe(topic, callback) {
        if (!this.subscriptions.has(topic)) {
            this.subscriptions.set(topic, new Set());
        }
        this.subscriptions.get(topic).add(callback);

        if (this.isConnected()) {
            await this.subscribeToTopic(topic);
        }
    }

    /**
     * Subscribes to a topic on the STOMP client.
     * @param {string} topic - Topic to subscribe to
     * @private
     */
    async subscribeToTopic(topic) {
        try {
            const topicData = this.subscriptions.get(topic);

            // Check if we already have a STOMP subscription for this topic
            if (topicData && topicData.subscription) {
                return;
            }

            if (!this.isConnected()) {
                return;
            }

            const subscription = this.stompClient.subscribe(topic, (frame) => {
                try {
                    const data = JSON.parse(frame.body);
                    this.notifySubscribers(topic, data);
                } catch (error) {
                    console.error('Error parsing message:', error);
                }
            });

            // Store the STOMP subscription reference
            this.subscriptions.get(topic).subscription = subscription;
        } catch (error) {
            console.error('Error subscribing to topic:', topic, error);
        }
    }

    /**
     * Notifies all subscribers of a topic with received data.
     * @param {string} topic - Topic that received the message
     * @param {*} data - Message data
     * @private
     */
    notifySubscribers(topic, data) {
        const subscribers = this.subscriptions.get(topic);
        if (subscribers) {
            subscribers.forEach((callback) => {
                if (callback !== 'subscription') {
                    callback(data);
                }
            });
        }
    }

    /**
     * Unsubscribes a callback from a topic.
     * @param {string} topic - Topic to unsubscribe from
     * @param {Function} callback - Callback to remove
     */
    unsubscribe(topic, callback) {
        const subscribers = this.subscriptions.get(topic);
        if (subscribers) {
            subscribers.delete(callback);
            if (subscribers.size === 0) {
                const subscription = subscribers.subscription;
                if (subscription) {
                    subscription.unsubscribe();
                }
                this.subscriptions.delete(topic);
            }
        }
    }

    /**
     * Unsubscribes from all topics.
     */
    unsubscribeAll() {
        for (const subscribers of this.subscriptions.values()) {
            const subscription = subscribers.subscription;
            if (subscription) {
                subscription.unsubscribe();
            }
        }
        this.subscriptions.clear();
    }

    /**
     * Resubscribes to all topics (used after reconnection).
     * @private
     */
    async resubscribeAll() {
        for (const topic of this.subscriptions.keys()) {
            await this.subscribeToTopic(topic);
        }
    }

    /**
     * Schedules a reconnection attempt with exponential backoff.
     * @private
     */
    scheduleReconnect() {
        this.reconnectAttempts++;
        // Cap at 10s: a longer backoff plus Chrome's background-tab throttling
        // left users disconnected (and silently losing events) for minutes
        const delay = Math.min(5000 * 2 ** this.reconnectAttempts, 10000);

        setTimeout(() => {
            // Don't call connect() directly as it needs a token
            // Instead, trigger reconnection through the store which handles token management
            if (this.reconnectionCallback) {
                this.reconnectionCallback();
            }
        }, delay);
    }

    /**
     * Sets the callback to use for reconnection attempts.
     * This should trigger a full reconnection through the store.
     * @param {Function} callback - Function to call for reconnection
     */
    setReconnectionCallback(callback) {
        this.reconnectionCallback = callback;
    }

    /**
     * Starts the heartbeat mechanism.
     * @private
     */
    startHeartbeat() {
        this.stopHeartbeat();
        this.heartbeatInterval = setInterval(() => {
            if (this.isConnected()) {
                try {
                    this.stompClient.publish({
                        destination: '/app/heartbeat',
                        body: JSON.stringify({ type: 'heartbeat' }),
                    });
                } catch (error) {
                    console.error('[WebSocket] Failed to send heartbeat:', error);
                }
            }
        }, 15000);
    }

    /**
     * Stops the heartbeat mechanism.
     * @private
     */
    stopHeartbeat() {
        if (this.heartbeatInterval) {
            clearInterval(this.heartbeatInterval);
            this.heartbeatInterval = null;
        }
    }

    /**
     * Disconnects the WebSocket connection and cleans up resources.
     */
    disconnect() {
        this.stopHeartbeat();
        this.unsubscribeAll();
        if (this.stompClient) {
            this.stompClient.deactivate();
        }
        this.socket = null;
        this.stompClient = null;
        this.notifyConnectionChange(false);
    }

    /**
     * Checks if the WebSocket connection is currently active.
     * @returns {boolean} Connection status
     */
    isConnected() {
        return this.stompClient && this.stompClient.connected;
    }
}

export default new WebSocketService();
